/* 
  From contentful you get a Rich Text Document, and you need instructions on how to render it.
  This file contains the instructions on how to render the Rich Text Document.

  Article and Case Study and any other place where you need to render rich text, should import this file.
*/

import React from "react";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import Image from "next/image";

// Client Components
import ImageCarousel from "@/components/client/ImageCarousel";
import CognitoForm from "@/components/client/CognitoForm";
import { Video } from "@/components/terra/component/Video/Video";

// Server Components
import Anchor from "@/components/server/Anchor";
import LibsynEmbed from "@/components/server/LibsynEmbed";
import ImageCompare from "@/components/server/ImageCompare";
import FormatSectionTestimonials from "@/components/client/SectionTestimonials/FormatSectionTestimonials";
import FormatActionBlock from "@/components/client/ActionBlock/FormatActionBlock";
import ArticleImage from "@/components/server/ArticleImage";
import FeaturedText from "@/components/server/FeaturedText";
import SectionStatBlocks from "@/components/server/SectionStatBlocks";
import ImageStrip from "@/components/server/ImageStrip";
import { EmbeddedEntryWrapper } from "@/components/client/EmbeddedEntryWrapper/EmbeddedEntryWrapper";
import ArticleCta from "@/components/server/ArticleCta";
import EmbedIFrame from "@/components/server/EmbedIFrame/EmbedIFrame";
import Link from "next/link";
import profile from "@/profiles/profileSingleton";
import MXPLayoutFaq from "@/components/formatters/MXPLayoutFaq";
import MXPEmbeddedRichTextTabs from "@/components/formatters/MXPEmbeddedRichTextTabs";
import MXPEmbeddedInfoList from "@/components/formatters/MXPEmbeddedInfoList";
import MXPEmbeddedFaqs from "@/components/formatters/MXPEmbeddedFaqs";
import MXPEmbeddedSectionFaqs from "@/components/formatters/MXPEmbeddedSectionFaqs";
import { slugify } from "@/lib/formatters/formatArticleAnchors";
import { processLinkUrl } from "@/lib/formatters/formatLinks";
import { buildRichTextLinkMaps } from "@/lib/formatters/richTextLinks";
import { localizeUrl } from "@/lib/url/localizeUrl";
import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale";
function extractNodeText(node: any): string {
  if (node.nodeType === "text") return node.value || "";
  if (Array.isArray(node.content)) {
    return node.content.map(extractNodeText).join("");
  }
  return "";
}
interface Options {
  preserveWhitespace: boolean;
  renderMark: any;
  renderNode: any;
}
function addKeysToChildren(content: React.ReactNode): React.ReactNode {
  if (!Array.isArray(content)) return content;
  return content.map((child, i) => React.isValidElement(child) ? React.cloneElement(child, {
    key: i
  } as any) : child);
}
export function createRichTextOptions(lang: string = DEFAULT_PUBLIC_LANG, links?: any): Options {
  const {
    entryHyperlink,
    assetHyperlink,
    entryBlock,
    assetBlock
  } = buildRichTextLinkMaps(links);
  return {
    preserveWhitespace: true,
    renderMark: {
      [MARKS.CODE]: (text: React.ReactNode) => {
        return <pre>
            <code>{addKeysToChildren(text)}</code>
          </pre>;
      },
      [MARKS.BOLD]: (text: React.ReactNode) => {
        return <strong className="bold">{addKeysToChildren(text)}</strong>;
      },
      [MARKS.UNDERLINE]: (text: React.ReactNode) => {
        return <u className="underline">{addKeysToChildren(text)}</u>;
      },
      [MARKS.ITALIC]: (text: React.ReactNode) => {
        return <em className="italic">{addKeysToChildren(text)}</em>;
      }
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node: any, children: React.ReactNode) => {
        return <h1 className="!mb-6 text-3xl font-bold">{children}</h1>;
      },
      [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => {
        const id = slugify(extractNodeText(node));
        return <h2 id={id} className="!mb-4 text-2xl font-bold">
            {children}
          </h2>;
      },
      [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => {
        return <h3 className="!mb-3 text-xl font-bold">{children}</h3>;
      },
      [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => {
        return <h4 className="!mb-2 text-lg font-bold">{children}</h4>;
      },
      [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => {
        return <h5 className="!mb-1 text-lg font-bold">{children}</h5>;
      },
      [BLOCKS.HEADING_6]: (node: any, children: React.ReactNode) => {
        return <h6 className="!mb-1 text-lg font-bold">{children}</h6>;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [BLOCKS.HR]: (node: any, children: React.ReactNode) => {
        return <hr className="my-8 border-gray-300" />;
      },
      [BLOCKS.QUOTE]: (node: any, children: React.ReactNode) => {
        return <blockquote className="my-4 border-l-4 border-blue-500 bg-gray-100 p-4">
            {children}
          </blockquote>;
      },
      [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
        const processedChildren = React.Children.map(children, (child, index) => <React.Fragment key={index}>{child}</React.Fragment>);
        return <p className="text-gray-m10 mb-8 text-base font-normal leading-8">
            {processedChildren}
          </p>;
      },
      [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => {
        return <ul className="list-disc pb-8 pl-7">{children}</ul>;
      },
      [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => {
        return <ol className="list-decimal pb-8 pl-7">{children}</ol>;
      },
      [BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => {
        return <li>{children}</li>;
      },
      [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
        const uri = node?.data?.uri;
        if (!uri) return <span>{children}</span>;
        if (uri == "ot-sdk-show-settings" && profile.NEXT_PUBLIC_ONETRUST_DISABLE !== true) {
          return <a key="ot-enabled" id="ot-sdk-btn" className="ot-sdk-show-settings !px-10 !py-4 !text-lg !no-underline hover:!bg-onetrust-green">
              {children}
            </a>;
        }
        const localizedUri = localizeUrl(uri, lang);
        const isExternal: boolean = uri.startsWith("http");
        return <Link href={localizedUri} className="underline" target={isExternal ? "_blank" : "_self"} rel={isExternal ? "noopener noreferrer" : ""}>
            {children}
          </Link>;
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children: React.ReactNode) => {
        const id = node?.data?.target?.sys?.id;
        const entry = id && entryHyperlink.get(id) || node?.data?.target;
        const href = processLinkUrl(entry, lang);
        if (!href) return <span>{children}</span>;
        return <Link href={href} className="underline">
            {children}
          </Link>;
      },
      [INLINES.ASSET_HYPERLINK]: (node: any, children: React.ReactNode) => {
        const id = node?.data?.target?.sys?.id;
        const asset = id && assetHyperlink.get(id) || node?.data?.target;
        const href = processLinkUrl(asset, lang);
        if (!href) return <span>{children}</span>;
        const isExternal = href.startsWith("http");
        return <Link href={href} className="underline" target={isExternal ? "_blank" : "_self"} rel={isExternal ? "noopener noreferrer" : ""}>
            {children}
          </Link>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        // GraphQL puts only a link stub in `json`; the entry itself arrives in
        // `links.entries.block`. REST-shaped documents already carry it inline.
        const id = node?.data?.target?.sys?.id;
        const target = id && entryBlock.get(id) || node?.data?.target;
        if (!target) {
          if (process.env.NEXT_PUBLIC_PROFILE?.includes("preview")) {
            return <div>Element removed or added incorrectly</div>;
          }
          return null;
        }
        const videoCode = target.videoCode;
        const type = target.__typename;
        if (type === "ArticleImage") {
          return <EmbeddedEntryWrapper childComponent={<ArticleImage url={target.mediaImage?.url} altText={target.altText} caption={documentToReactComponents(target?.imageCaption?.json)} />} extraClasses={`relative`} />;
        } else if (type === "Image") {
          return <EmbeddedEntryWrapper childComponent={<ArticleImage url={target?.image?.url} altText={target?.altText} />} extraClasses={`relative`} />;
        } else if (type === "Video") {
          return <EmbeddedEntryWrapper childComponent={<Video videoId={videoCode} />} extraClasses={`flex justify-center`} />;
        } else if (type === "ArticleCta") {
          return <EmbeddedEntryWrapper childComponent={<ArticleCta data={target} lang={lang} />} extraClasses={`relative`} />;
        } else if (type === "ImageCompare") {
          return <ImageCompare url1={target?.image1?.url} url2={target?.image2?.url} />;
          // return (
          //   <EmbeddedEntryWrapper
          //     childComponent={ <ImageCompare url1={node.data.target.image1.url} url2={node.data.target.image2.url} /> }
          //     extraClasses={`relative`}
          //   />
          // )
        } else if (type === "Carousel") {
          const carouselItems = target?.carouselItemsCollection?.items ?? [];
          if (carouselItems.length === 0) return null;
          if (carouselItems.length == 1) {
            const url = carouselItems[0]?.url;
            return <EmbeddedEntryWrapper childComponent={<Image src={url || ""} width={0} height={0} alt={carouselItems[0]?.description || ""} sizes="100vw" style={{
              width: "inherit",
              height: "inherit"
            }} />} extraClasses={`relative`} />;
          }
          if (target?.displayAs === "Strip") {
            return <EmbeddedEntryWrapper childComponent={<ImageStrip items={carouselItems} />} extraClasses={`relative`} />;
          }
          return <EmbeddedEntryWrapper childComponent={<ImageCarousel items={carouselItems} />} extraClasses={`relative`} />;
        } else if (type === "Image") {
          return <EmbeddedEntryWrapper childComponent={<Image src={target?.image?.url || ""} alt={target?.altText || ""} width={1280} height={800} sizes="100vw" className={"caseStudy-content-image"} />} extraClasses={`relative`} />;
        } else if (type === "ActionBlock") {
          return <EmbeddedEntryWrapper childComponent={<FormatActionBlock actionBlockData={target} isEmbedded />} extraClasses={`narrow-container`} />;
        } else if (type === "ArticleAnchor") {
          const anchorId = target?.anchorId;
          return <Anchor anchorId={anchorId} />;
        } else if (type === "LibsynEmbed") {
          const code = target?.libsynCode;
          return <LibsynEmbed embedCode={code} />;
        } else if (type === "FeaturedText") {
          return <EmbeddedEntryWrapper childComponent={<FeaturedText data={target} lang={lang} />} />;
        } else if (type === "SectionStatBlocks") {
          return <EmbeddedEntryWrapper childComponent={<SectionStatBlocks data={target} />} />;
        } else if (type === "CognitoForm") {
          return <EmbeddedEntryWrapper childComponent={<CognitoForm cognitoScriptID={target?.scriptId} cognitoFormID={target?.formId} />} />;
        } else if (type === "SectionTestimonials") {
          return <EmbeddedEntryWrapper childComponent={<FormatSectionTestimonials data={target} />} />;
        } else if (type === "LayoutFaq") {
          return <EmbeddedEntryWrapper childComponent={<MXPLayoutFaq value={target} key={target?.id} divider={false} lang={lang} />} />;
        } else if (type === "RichTextTabs") {
          return <EmbeddedEntryWrapper childComponent={<MXPEmbeddedRichTextTabs value={target} lang={lang} />} />;
        } else if (type === "InfoList") {
          return <EmbeddedEntryWrapper childComponent={<MXPEmbeddedInfoList value={target} lang={lang} />} />;
        } else if (type === "Faqs") {
          return <EmbeddedEntryWrapper childComponent={<MXPEmbeddedFaqs value={target} />} />;
        } else if (type === "SectionFaqs") {
          return <EmbeddedEntryWrapper childComponent={<MXPEmbeddedSectionFaqs value={target} lang={lang} />} />;
        } else if (type === "EmbedIFrame") {
          return <EmbeddedEntryWrapper childComponent={<EmbedIFrame url={target.url} title={target.title} height={target.height} width={target.width} allow={target.allow} sandbox={target.sandbox} />} />;
        } else if (type === null || type === undefined) {
          // if elements are broken or archived, this will be null or undefined
          if (process.env.NEXT_PUBLIC_PROFILE?.includes("preview")) {
            return <div>Element removed or added incorrectly</div>;
          } else {
            return null; // don't show outside of preview
          }
        } else {
          return `ERROR: Unknown Entry Type: ${type}`;
        }
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        const id = node?.data?.target?.sys?.id;
        const target = id && assetBlock.get(id) || node?.data?.target;
        const url = target?.url;
        if (!url) {
          if (process.env.NEXT_PUBLIC_PROFILE?.includes("preview")) {
            return <div>Element removed or added incorrectly</div>;
          }
          return null;
        }
        // contentType comes from GraphQL; REST-shaped targets only carry dimensions.
        const isImage = target.contentType ? target.contentType.startsWith("image/") : Boolean(target.width && target.height);
        if (!isImage) {
          return <Link href={url} className="underline" target="_blank" rel="noopener noreferrer">
              {target.title || url}
            </Link>;
        }
        return <EmbeddedEntryWrapper childComponent={<Image src={url} alt={target.description || target.title || ""} width={target.width || 0} height={target.height || 0} sizes="100vw" className="h-auto w-full" />} extraClasses={`relative`} />;
      }
    }
  };
}

/** @deprecated Use `createRichTextOptions(lang)` for locale-aware links */
export const generalOptions: Options = createRichTextOptions(DEFAULT_PUBLIC_LANG);